/**
 * Monte Carlo VBL prediction using Johnson's method parameter uncertainty
 */

import {
  extractJohnsonUncertainty,
  sampleJohnsonParameters,
  validateJohnsonUncertainty
} from './johnsonParameterUncertainty';
import { calculateReliability } from './weibull';
import { VblAnalysisResult } from '@/types/vbl';
import {
  calculateSurvivalAdjustment,
  adjustPercentilesForSurvival,
  type SurvivalAdaptationConfig
} from './survivalAdaptation';

export interface MonteCarloVBLOptions extends SurvivalAdaptationConfig {
  numSamples: number;
  confidenceLevel: number;
  samplingMethod: 'rejection' | 'inverse-transform' | 'hybrid';
  rejectionSampleLimit: number;
  useLogNormalScale: boolean;
  correlationThreshold: number;
  covarianceReductionFactor?: number; // Reduce parameter uncertainty for narrower CIs
  usePointEstimates?: boolean; // Use only point estimates (no parameter uncertainty)
}

export const DEFAULT_MONTE_CARLO_OPTIONS: MonteCarloVBLOptions = {
  numSamples: 1000,
  confidenceLevel: 0.95,
  samplingMethod: 'hybrid',
  rejectionSampleLimit: 1000,
  useLogNormalScale: true,
  correlationThreshold: 0.1,
  covarianceReductionFactor: 0.7, // Default 30% reduction in parameter uncertainty
  usePointEstimates: false, // Default to parameter sampling
  // Survival adaptation settings
  useSurvivalAdaptation: true,
  survivalAdaptationStrength: 0.3,
  earlyStageThreshold: 0.4
};

/**
 * Get sample-size-based empirical safety factors
 * Larger samples get lower safety factors (more trust in data)
 */
function getEmpiricalSafetyFactors(sampleSize: number): { medianFactor: number; percentile95Factor: number } {
  if (sampleSize >= 50) {
    // Large samples: Minimal safety margins - trust the data
    return { medianFactor: 1.1, percentile95Factor: 1.05 };
  } else if (sampleSize >= 20) {
    // Medium samples: Moderate safety margins
    return { medianFactor: 1.3, percentile95Factor: 1.2 };
  } else {
    // Small samples: Conservative safety margins (original behavior)
    return { medianFactor: 1.5, percentile95Factor: 1.1 };
  }
}

/**
 * Get sample-size-based survival adaptation settings
 * Larger samples get reduced survival adaptation (less CI narrowing)
 */
function getSurvivalAdaptationSettings(sampleSize: number): { survivalAdaptationStrength: number; earlyStageThreshold: number } {
  if (sampleSize >= 50) {
    // Large samples: Minimal survival adaptation - trust the statistical distribution
    return { survivalAdaptationStrength: 0.1, earlyStageThreshold: 0.6 };
  } else if (sampleSize >= 20) {
    // Medium samples: Moderate survival adaptation
    return { survivalAdaptationStrength: 0.2, earlyStageThreshold: 0.5 };
  } else {
    // Small samples: Full survival adaptation (original behavior)
    return { survivalAdaptationStrength: 0.3, earlyStageThreshold: 0.4 };
  }
}

export interface VBLResult {
  median: number;
  mean: number;
  confidenceInterval: {
    lower: number;
    upper: number;
  };
  probabilityOfFailureBy: Array<{
    days: number;
    probability: number;
  }>;
  simulationMetadata: {
    samplesGenerated: number;
    rejectionEfficiency?: number;
    convergenceAchieved: boolean;
    samplingMethod: string;
  };
}

export function weibullRandom(shape: number, scale: number): number {
  const u = Math.random();
  return scale * Math.pow(-Math.log(1 - u), 1 / shape);
}

export function rejectionSampleConditional(
  shape: number,
  scale: number,
  tSurvived: number,
  maxAttempts: number = 1000
): number | null {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const T = weibullRandom(shape, scale);
    if (T > tSurvived) {
      return T;
    }
  }
  return null; // Failed to find valid sample within maxAttempts
}

/**
 * Sample conditional failure time using inverse transform method
 * Uses the formula: T = η[-log(S_ts × (1-U))]^(1/β)
 * Where S_ts = exp(-(t_survived/η)^β) is survival probability at current time
 */
export function inverseTransformConditional(
  shape: number,
  scale: number,
  tSurvived: number
): number {
  // Calculate survival probability at current time (reuse existing function)
  const survivalAtCurrent = calculateReliability(tSurvived, shape, scale);

  if (survivalAtCurrent <= 0.001) {
    // Already at very low survival probability, return immediate failure
    return tSurvived;
  }

  // Sample from conditional distribution
  const u = Math.random();
  const conditionedU = survivalAtCurrent * (1 - u) + (1 - survivalAtCurrent);

  // Inverse Weibull CDF
  const failureTime = scale * Math.pow(-Math.log(1 - conditionedU), 1 / shape);

  return Math.max(tSurvived, failureTime);
}

/**
 * Sample conditional failure time using hybrid approach
 * First tries rejection sampling, falls back to inverse transform if inefficient
 */
export function hybridSampleConditional(
  shape: number,
  scale: number,
  tSurvived: number,
  rejectionLimit: number = 100
): { value: number; method: 'rejection' | 'inverse-transform' } {
  // Try rejection sampling first (more accurate for extreme survival probabilities)
  const rejectionResult = rejectionSampleConditional(shape, scale, tSurvived, rejectionLimit);

  if (rejectionResult !== null) {
    return { value: rejectionResult, method: 'rejection' };
  }

  // Fall back to inverse transform (always works)
  const inverseResult = inverseTransformConditional(shape, scale, tSurvived);
  return { value: inverseResult, method: 'inverse-transform' };
}


/**
 * Calculate percentile from sorted array (reuse pattern from parametricBootstrap.ts)
 */
function calculatePercentile(sortedArray: number[], percentile: number): number {
  const index = Math.floor(percentile * sortedArray.length);
  return sortedArray[Math.min(index, sortedArray.length - 1)] || 0;
}

/**
 * Monte Carlo VBL calculation for VBL analysis using Johnson's method
 * Orchestrates the entire pipeline using Johnson's method parameter uncertainty
 */

export function calculateMonteCarloVBL(
  vblResult: VblAnalysisResult,
  currentDegradationTime: number,
  options: MonteCarloVBLOptions = DEFAULT_MONTE_CARLO_OPTIONS
): VBLResult | null {

  // Step 1: Extract Johnson's method parameter uncertainty
  const uncertainty = extractJohnsonUncertainty(vblResult);
  if (!uncertainty) {
    console.warn('Could not extract Johnson parameter uncertainty for Monte Carlo VBL');
    return null;
  }

  // Validate the uncertainty
  const validation = validateJohnsonUncertainty(uncertainty);
  if (validation.warnings.length > 0) {
    console.warn('Johnson parameter uncertainty warnings:', validation.warnings);
  }

  // Calculate empirical bounds from VBL data for realistic upper bounds
  const vblDegradationTimes = vblResult.plotData.empirical.map(p => p.time);
  const _empiricalMax = Math.max(...vblDegradationTimes);
  const empiricalMedian = vblDegradationTimes.sort((a, b) => a - b)[Math.floor(vblDegradationTimes.length / 2)];
  const empirical95th = vblDegradationTimes.sort((a, b) => a - b)[Math.floor(vblDegradationTimes.length * 0.95)];

  // Calculate realistic maximum VBL with adaptive safety factors based on sample size
  const sampleSize = vblResult.plotData.empirical.length;
  const safetyFactors = getEmpiricalSafetyFactors(sampleSize);

  // Calculate max VBL with consideration for current degradation time
  // Ensures realistic predictions for turbines that have already survived significant time
  const maxRealisticVBL = Math.max(
    (empiricalMedian * safetyFactors.medianFactor) - currentDegradationTime,
    (empirical95th * safetyFactors.percentile95Factor) - currentDegradationTime,
    empiricalMedian * 0.5
  );


  // Step 2: Sample parameters from Johnson's uncertainty distribution with optional point estimates mode
  let parameterSamples: Array<{ shape: number; scale: number }>;

  if (options.usePointEstimates) {
    // Point estimates mode: use only the point estimates (no parameter uncertainty)
    parameterSamples = Array(options.numSamples).fill({
      shape: uncertainty.shape.estimate,
      scale: uncertainty.scale.estimate
    });
  } else {
    // Full uncertainty mode: sample parameters from uncertainty distribution
    parameterSamples = sampleJohnsonParameters(
      uncertainty,
      options.numSamples,
      options.useLogNormalScale,
      options.covarianceReductionFactor || 1.0
    );
  }

  if (parameterSamples.length === 0) {
    return null;
  }

  return performMonteCarloVBLCalculation(parameterSamples, currentDegradationTime, options, maxRealisticVBL, sampleSize);
}



/**
 * Core Monte Carlo VBL calculation logic using parameter samples
 */
function performMonteCarloVBLCalculation(
  parameterSamples: Array<{ shape: number; scale: number }>,
  currentDegradationTime: number,
  options: MonteCarloVBLOptions,
  maxRealisticVBL?: number,
  sampleSize?: number
): VBLResult {

  // Step 3: Generate conditional failure times and calculate VBL
  const vblSamples: number[] = [];
  const samplingMethods: string[] = [];
  let rejectionSuccesses = 0;

  for (const params of parameterSamples) {
    let failureTime: number;
    let method: string;

    switch (options.samplingMethod) {
      case 'rejection': {
        const rejectionResult = rejectionSampleConditional(
          params.shape,
          params.scale,
          currentDegradationTime,
          options.rejectionSampleLimit
        );
        if (rejectionResult !== null) {
          failureTime = rejectionResult;
          method = 'rejection';
          rejectionSuccesses++;
        } else {
          // Fallback to inverse transform
          failureTime = inverseTransformConditional(
            params.shape,
            params.scale,
            currentDegradationTime
          );
          method = 'inverse-transform-fallback';
        }
        break;
      }

      case 'inverse-transform': {
        failureTime = inverseTransformConditional(
          params.shape,
          params.scale,
          currentDegradationTime
        );
        method = 'inverse-transform';
        break;
      }

      case 'hybrid':
      default: {
        const hybridResult = hybridSampleConditional(
          params.shape,
          params.scale,
          currentDegradationTime,
          options.rejectionSampleLimit
        );
        failureTime = hybridResult.value;
        method = hybridResult.method;
        if (method === 'rejection') rejectionSuccesses++;
        break;
      }
    }

    // Calculate VBL = T_total - t_survived
    let vbl = failureTime - currentDegradationTime;
    vbl = Math.max(0, vbl);

    // Apply empirical upper bound if provided - only for small datasets
    // Large datasets are trusted to extend naturally
    if (maxRealisticVBL !== undefined && vbl > maxRealisticVBL && (sampleSize || 0) < 25) {
      vbl = maxRealisticVBL;
    }

    vblSamples.push(vbl);
    samplingMethods.push(method);
  }

  // Step 4: Calculate statistics with fixed percentiles for narrower CIs
  vblSamples.sort((a, b) => a - b);

  // Calculate median and mean
  const mean = vblSamples.reduce((sum, val) => sum + val, 0) / vblSamples.length;
  const median = calculatePercentile(vblSamples, 0.5);

  // Calculate survival adjustment with sample-size-based settings
  const empiricalMedian = Math.max(...parameterSamples.map(p => p.scale)) * 0.693; // Approximate median from scale

  // Get adaptive survival settings based on sample size
  const adaptiveSurvivalSettings = getSurvivalAdaptationSettings(sampleSize || 0);
  const adaptiveOptions = {
    ...options,
    survivalAdaptationStrength: adaptiveSurvivalSettings.survivalAdaptationStrength,
    earlyStageThreshold: adaptiveSurvivalSettings.earlyStageThreshold
  };

  const survivalAdjustment = calculateSurvivalAdjustment(
    currentDegradationTime,
    empiricalMedian,
    adaptiveOptions
  );

  // Apply survival adaptation to percentiles
  // Use confidence level to determine percentiles (e.g., 90% CI uses 5th and 95th percentiles)
  const alpha = 1 - (options.confidenceLevel || 0.90);
  let lowerPercentile = alpha / 2;  // e.g., 0.05 for 90% CI
  let upperPercentile = 1 - (alpha / 2);  // e.g., 0.95 for 90% CI

  if (adaptiveOptions.useSurvivalAdaptation && survivalAdjustment.adjustmentFactor < 1.0) {
    const adjusted = adjustPercentilesForSurvival(
      lowerPercentile,
      upperPercentile,
      survivalAdjustment.adjustmentFactor
    );
    lowerPercentile = adjusted.adjustedLower;
    upperPercentile = adjusted.adjustedUpper;

  }

  // Calculate bounds from percentiles
  const statisticalUpper = calculatePercentile(vblSamples, upperPercentile);
  let upperBound = statisticalUpper;
  let lowerBound = calculatePercentile(vblSamples, lowerPercentile);

  // Only cap upper bound if it's unreasonably high (more than 2x max realistic VBL)
  if (maxRealisticVBL && upperBound > maxRealisticVBL * 2) {
    upperBound = maxRealisticVBL * 2; // Only cap extreme outliers
  }

  // Apply minimum CI width protection
  // Ensure CI is at least 20% of median VBL or 30 days (whichever is larger)
  const minCIWidth = Math.max(median * 0.2, 30);
  const currentCIWidth = upperBound - lowerBound;

  if (currentCIWidth < minCIWidth) {
    const expansion = (minCIWidth - currentCIWidth) / 2;
    lowerBound = Math.max(1, lowerBound - expansion);
    upperBound = upperBound + expansion;
  }

  // Ensure upper bound extends at least as far from median as lower bound
  // This prevents asymmetric CIs where upper = median
  const lowerDistance = median - lowerBound;
  const upperDistance = upperBound - median;

  if (upperDistance < lowerDistance * 0.8) { // Upper is too close to median
    upperBound = median + lowerDistance; // Make roughly symmetric
  }


  // Step 5: Calculate probability of failure over time horizons
  const timeHorizons = [7, 14, 30, 60, 90, 180, 365]; // Days
  const probabilityOfFailureBy = timeHorizons.map(days => {
    const failuresBeforeHorizon = vblSamples.filter(vbl => vbl <= days).length;
    return {
      days,
      probability: failuresBeforeHorizon / vblSamples.length
    };
  });

  // Calculate efficiency metrics
  const rejectionEfficiency = options.samplingMethod === 'rejection' || options.samplingMethod === 'hybrid'
    ? rejectionSuccesses / parameterSamples.length
    : undefined;

  return {
    median,
    mean,
    confidenceInterval: {
      lower: lowerBound,
      upper: upperBound
    },
    probabilityOfFailureBy,
    simulationMetadata: {
      samplesGenerated: vblSamples.length,
      rejectionEfficiency,
      convergenceAchieved: true, // Simplified for now - could add convergence checking
      samplingMethod: options.samplingMethod
    }
  };
}